import type { ArticleModel } from 'data-types-config';
import { getDate, getNumericDate } from '../../utils/useFormatDate';

export class Article {
  public readonly id: number;
  public readonly title: string;
  public readonly content: string;
  public readonly category: string;
  public readonly date: Date;
  public readonly image: string;
  public readonly link: string;
  public readonly linkLabel: string;
  public readonly slug?: string;
  public readonly truncateContent: string;
  public readonly categoryKey?: string;
  public readonly postType?: string;

  constructor(model: ArticleModel) {
    this.id = model.id;
    this.title = model.title;
    this.content = model.content;
    this.category = model.category;
    this.date = new Date(model.date);
    this.image = model.image;
    this.link = model.link;
    this.linkLabel = model.linkLabel;
    this.slug = model.slug;
    this.truncateContent =
      model.truncateContent || this.truncateText(this.content, 100);
    this.categoryKey = model.categoryKey;
    this.postType = model.postType;
  }

  /**
   * Returns a formatted version of the date for display (MM/DD/YYYY).
   */
  get formattedDate(): string {
    return getNumericDate(this.date);
  }

  /**
   * Business logic: Checks if the article was published in the last 30 days.
   */
  get isRecent(): boolean {
    const thirtyDaysAgo = new Date();
    thirtyDaysAgo.setDate(thirtyDaysAgo.getDate() - 30);
    return this.date > thirtyDaysAgo;
  }

  /**
   * Business logic: Calculates estimated reading time.
   */
  get readingTimeMinutes(): number {
    const wordsPerMinute = 200;
    const words = this.content.split(/\s+/g).length;
    return Math.ceil(words / wordsPerMinute);
  }

  /**
   * Domain Logic: Determines if the article has a featured image.
   */
  get hasImage(): boolean {
    return !!this.image && this.image !== '';
  }

  /**
   * Domain Logic: Returns the correct route prefix based on the category or post type.
   */
  get routePrefix(): string {
    if (this.categoryKey === 'beliefs') return 'beliefs';
    if (this.categoryKey === 'elevenPoints') return 'elevenpoints';
    if (this.postType === 'page') return 'elevenpoints';
    return 'articles';
  }

  // Method to truncate content
  private truncateText(text: string, length: number): string {
    if (!text) return '';
    if (text.length <= length) return text;
    return text.slice(0, length) + '...';
  }
}
